<template>
    <div class="row">
        <div class="col-12" v-show="showDatePicker">
            <div class="form-group mb-2">
                <label class="col-form-label" for="valid_from">
                    {{ $t('shop.validity_start') }}
                </label>
                <div class="datepicker-overlay">
                    <nova-datepicker
                        id="valid_from"
                        name="validity_start"
                        required="required"
                        :config="datePickerPreset"
                        :mobileIcon="true"
                        @dp-change="
                            $emit('validity-start-change', vignetteValidFrom);
                            $emit('display-type-options');
                            $emit('input-change', 'validity_start');
                        "
                        @close="
                        setVignetteValidFromNullSetting = false;
                        validityFromNotice = null;
                        "
                        :class="{ 'is-invalid': formErrors.validity_start }"
                        v-model="vignetteValidFrom"
                        ref="validFrom"
                        @dp-invalid-date="$emit('set-invalid-valid-from-message', $event);"
                        :disable-placeholder="setVignetteValidFromNullSetting"
                    >
                    </nova-datepicker>
                    <div class="invalid-feedback">
                        <div v-if="formErrors.validity_start && !vignetteValidFrom">
                            {{ $t('shop.validity_from_input_empty_error') }}
                        </div>
                        <div v-else>
                            <span class="d-block" v-for="error in formErrors.validity_start" :key="error">
                                {{ error }}
                            </span>
                        </div>
                    </div>
                    <div class="invalid-validity-start" v-if="validityFromNotice">
                        <span class="d-block" v-html="validityFromNotice"></span>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="validToDate && Object.keys(validToDate).length > 0"
            class="col-12 text-center"
        >
            <div class="vignette-validity-until mt-0 mb-4 h5 bg-light rounded py-2">
                <span class="prefix-label">{{ $t('shop.validityPrefix') }}</span>
                <span class="from-label">{{ vignetteValidFrom }}</span>
                <span class="separator-label">{{ $t('shop.validitySeparator') }}</span>
                <span class="to-date-label">{{ validToDate.date }}</span>
                <span class="to-time-label">{{ validToDate.time }}</span>
            </div>
        </div>
        <div class="col-12">
            <div class="form-group">
                <label class="d-block">{{ $t('shop.vehicle_type') }}</label>
                <div class="row justify-content-center">
                    <div
                        v-for="vignetteOption in sortedVignetteOptions"
                        :key="vignetteOption.id"
                        class="col-12 col-md-6 mb-2"
                        :class="{
                            'text-md-right': 0 === getIndexForVignetteOption(vignetteOption.id),
                        }"
                    >
                        <div class="checkbox-button-container">
                            <input
                                type="radio"
                                class="checkbox"
                                name="option_id"
                                :value="vignetteOption.id"
                                :id="'vignette_option_' + vignetteOption.id"
                                :class="{ 'is-invalid': formErrors.option_id }"
                                :price="vignetteOption.price.toFixed(2)"
                                @change="onChange()"
                                v-model="selectedVignetteOption"
                                required
                            />
                            <label
                                :for="'vignette_option_' + vignetteOption.id"
                                class="mb-0 shop-product-card alternative bg-white px-4 py-2 rounded text-center text-reset-case"
                                :class="{
                                    highlighted: vignetteOption.id === selectedVignetteOption,
                                }"
                            >
                                <i
                                    class="mdi h5 mr-1"
                                    :class="{
                                        'mdi-car': vignetteOption.vehicle_type === 'car',
                                        'mdi-motorbike': vignetteOption.vehicle_type === 'bike',
                                        'mdi-truck-trailer': vignetteOption.vehicle_type === 'trailer' || vignetteOption.vehicle_type === 'trailer-heavy',
                                        'mdi-rv-truck': vignetteOption.vehicle_type === 'camper',
                                        'mdi-bus-side': [
                                            'bus-1',
                                            'bus-2',
                                            'bus-3',
                                            'bus-4',
                                        ].indexOf(vignetteOption.vehicle_type) !== -1,
                                        'mdi-tent': vignetteOption.vehicle_type === 'circus',
                                        'mdi-speedometer-slow': vignetteOption.vehicle_type === 'slow',
                                        'mdi-tractor': vignetteOption.vehicle_type === 'tractor',
                                        'mdi-car-pickup': vignetteOption.vehicle_type === 'car-heavy',
                                    }"
                                ></i>
                                <h2 class="title mt-2 mb-0">
                                    {{ $t('shop.vehicle_' + vignetteOption.vehicle_type) }}
                                </h2>
                                <small
                                    class="mt-1 mb-0"
                                    v-if="$te('vehicleTypes.' + vignetteOption.vehicle_type + '_description')"
                                    v-t="'vehicleTypes.' + vignetteOption.vehicle_type + '_description'">
                                </small>
                            </label>

                            <Tooltip
                                v-if="getVehicleInfo(vignetteOption) !== null"
                                :key="`${vignetteOption.id}_vehicleInfo`"
                                class="vehicle-info-tooltip"
                            >
                                <template #icon>
                                    <i class="icon mdi mdi-information-outline"></i>
                                </template>

                                <template #text>
                                    <span v-html="getVehicleInfo(vignetteOption)"></span>
                                </template>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Tooltip from '../../Layout/Tooltip.vue';

export default {
    name: 'VignetteOption',

    components: {
        Tooltip,
    } ,

    props: [
        'datePickerOptions',
        'formErrors',
        'validFromNotice',
        'validTo',
        'validFrom',
        'rawValidFrom',
        'options',
        'selectedOption',
        'showDatePicker',
        'setValidFromNull',
    ],

    data() {
        return {
            validityFromNotice: this.validFromNotice,
            vignetteValidFrom: this.setValidFromNull ? null : this.validFrom.date,
            selectedVignetteOption: this.selectedOption,
            datePickerPreset: this.datePickerOptions,
            setVignetteValidFromNullSetting: this.setValidFromNull,
            validToDate: this.setValidFromNull ? null : this.validTo,
        };
    },

    computed: {
        sortedVignetteOptions() {
            const sortedVignetteOptions = Object.values(this.options);

            if (!sortedVignetteOptions) {
                return null;
            }

            // Apply the sorting of the items.
            return sortedVignetteOptions.toSorted((item1, item2) => {
                // The sorting values are equal.
                if (item1.vehicle_type === item2.vehicle_type) {
                    return 0;
                }

                return item1.vehicle_type < item2.vehicle_type ? 1 : -1;
            });
        },
    },

    watch: {
        selectedOption(val) {
            this.selectedVignetteOption = val;
        },
        vignetteValidFrom() {
            if (this.setVignetteValidFromNullSetting && this.products[this.selectedProduct].is_validity_controllable) {
                this.vignetteValidFrom = null;
                this.validToDate = null;
            }

            this.formErrors.validity_start = null;
        },

        validTo() {
            this.validToDate = this.validTo;
        },
    },

    methods: {
        saveToSession(key, value, type) {
            this.$emit('save-to-session', {key, value, type});
        },

        getIndexForVignetteOption(id) {
            id = '' + id;

            const ids = Object.keys(this.options);
            for (let i = 0; i < ids.length; i++) {
                if (ids[i] === id) {
                    return i;
                }
            }

            return -1;
        },

        getVehicleInfo(option) {
            if (typeof option.vehicle_type === 'undefined') {
                return null;
            }

            const varName = `shop.vehicle_${option.vehicle_type}Info`;
            const content = this.$t(varName);

            if (content === varName || typeof content !== 'string' || content.length === 0) {
                return null;
            }

            return content;
        },

        onChange() {
            this.$emit('option-selected', this.selectedVignetteOption);
            this.$emit('display-type-options');
            this.$emit('input-change', 'option_id');
            this.saveToSession('option_id', 'string', this.selectedVignetteOption);
            this.$emit(
                'price-change',
                this.options[this.selectedVignetteOption].price
            );
            this.$emit(
                'service-fee-change',
                this.options[this.selectedVignetteOption].service_fee
            );
            this.$emit(
                'insurance-fee-change',
                this.options[this.selectedVignetteOption].insurance_fee
            );
            this.$emit('update-date-picker');
        },
    },
};
</script>
